"use client";

import { useEffect, useState } from "react";
import { Check, Loader2, Play, TriangleAlert } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import type { CollectorRun } from "@/lib/types";
import { DATA_REFRESHED_EVENT } from "@/lib/data-refresh";

type Phase = "idle" | "starting" | "running" | "success" | "error";

const ACTIVE_STATES = new Set(["queued", "running"]);

export function CollectButton({ collectorRun }: { collectorRun: CollectorRun | null }) {
  const { t } = useI18n();
  const initiallyActive = Boolean(collectorRun && ACTIVE_STATES.has(collectorRun.state));
  const [phase, setPhase] = useState<Phase>(initiallyActive ? "running" : "idle");
  const [acceptedAt, setAcceptedAt] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const busy = phase === "starting" || phase === "running";

  useEffect(() => {
    if (!busy) return;

    let cancelled = false;
    const poll = async () => {
      try {
        const response = await fetch("/api/collector/status", {
          cache: "no-store",
          credentials: "include"
        });
        if (!response.ok) return;
        const payload = (await response.json()) as { run: CollectorRun | null };
        const run = payload.run;
        if (!run) return;

        const belongsToRequest = !acceptedAt
          || new Date(run.created_at).getTime() >= new Date(acceptedAt).getTime() - 2_000;
        if (!belongsToRequest) return;

        if (ACTIVE_STATES.has(run.state)) {
          if (!cancelled) setPhase("running");
          return;
        }
        if (cancelled) return;
        if (run.state === "success" || run.state === "partial_success") {
          setPhase("success");
          setMessage(t("collector.complete"));
        } else {
          setPhase("error");
          setMessage(run.reason_code ? `${t("collector.failed")}: ${run.reason_code}` : t("collector.failed"));
        }
        window.dispatchEvent(new Event(DATA_REFRESHED_EVENT));
      } catch {
        // A transient status request must not interrupt an in-progress collector.
      }
    };

    const initialTimer = window.setTimeout(poll, 1_500);
    const interval = window.setInterval(poll, 4_000);
    return () => {
      cancelled = true;
      window.clearTimeout(initialTimer);
      window.clearInterval(interval);
    };
  }, [acceptedAt, busy, t]);

  async function runCollector() {
    setPhase("starting");
    setMessage(null);
    try {
      const response = await fetch("/api/collector/run", {
        method: "POST",
        credentials: "include"
      });
      const payload = (await response.json().catch(() => ({}))) as {
        accepted_at?: string;
        detail?: string;
      };
      if (response.status === 409) {
        setAcceptedAt(null);
        setPhase("running");
        setMessage(t("collector.already_running"));
        return;
      }
      if (!response.ok) throw new Error(payload.detail || `${t("collector.failed")} (${response.status})`);
      setAcceptedAt(payload.accepted_at ?? new Date().toISOString());
      setPhase("running");
      setMessage(t("collector.started"));
    } catch (error) {
      setPhase("error");
      setMessage(error instanceof Error ? error.message : t("collector.failed"));
    }
  }

  const Icon = phase === "success" ? Check : phase === "error" ? TriangleAlert : busy ? Loader2 : Play;
  const label = busy ? t("collector.running") : t("collector.run");

  return (
    <div className="relative">
      <button
        type="button"
        onClick={runCollector}
        disabled={busy}
        title={t("collector.separate_schedule")}
        className="inline-flex min-h-9 items-center gap-2 rounded-md border border-[var(--hc-border-strong)] bg-[var(--hc-surface-2)] px-3 text-xs font-semibold text-[var(--hc-text)] transition-colors hover:border-[var(--hc-blue)] hover:bg-[var(--hc-blue-soft)] disabled:cursor-wait disabled:opacity-75"
      >
        <Icon size={14} className={busy ? "animate-spin" : undefined} aria-hidden="true" />
        <span className="hidden sm:inline">{label}</span>
        <span className="sm:hidden">{busy ? t("collector.running_short") : t("collector.run_short")}</span>
      </button>
      {message ? (
        <div
          role="status"
          aria-live="polite"
          className={`absolute right-0 top-full z-30 mt-2 w-64 rounded-md border bg-[var(--hc-surface-2)] px-3 py-2 text-xs shadow-xl ${phase === "error" ? "border-[var(--hc-negative)] text-[var(--hc-negative)]" : "border-[var(--hc-border-strong)] text-[var(--hc-muted)]"}`}
        >
          {message}
        </div>
      ) : null}
    </div>
  );
}
